"use client"

import { useEffect, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import ReminderService from '@/api/reminder.service';
import { Loader2, Plus, Filter, Clock, Bell, Calendar, CheckCircle2, Calendar as CalendarIcon } from 'lucide-react';
import { 
  Card, 
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import FormModal from '@/components/FormModal';
import CreateReminderForm from './CreateReminderForm';

export default function Reminders() {
  const [reminders, setReminders] = useState<any[]>([]);
  const [filteredReminders, setFilteredReminders] = useState<any[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { toast } = useToast();
  
  const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
  const businessId = selectedBusiness?.id;
  
  useEffect(() => {
    if (businessId) {
      fetchReminders();
    }
  }, [businessId]);
  
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredReminders(reminders);
    } else {
      setFilteredReminders(reminders.filter(reminder => reminder.type === activeFilter));
    }
  }, [activeFilter, reminders]);
  
  const fetchReminders = async () => {
    setIsLoading(true);
    try {
      const response = await ReminderService.getAllReminders(businessId);
      
      if (response.data.result === 'success') {
        setReminders(response.data.reminders || []);
        setFilteredReminders(response.data.reminders || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch reminders",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching reminders:", error);
      toast({
        title: "Error",
        description: "An error occurred while fetching reminders",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleMarkComplete = async (reminderId: number) => {
    try {
      const response = await ReminderService.markReminderAsCompleted(reminderId);
      
      if (response.data.result === 'success') {
        // Optimistic update
        const updatedReminders = reminders.map(reminder => 
          reminder.id === reminderId 
            ? { ...reminder, status: 'completed', completedAt: new Date().toISOString() } 
            : reminder
        );
        setReminders(updatedReminders);
        
        toast({
          title: "Success",
          description: "Reminder marked as completed",
        });
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to update reminder",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error marking reminder as completed:", error);
      toast({
        title: "Error",
        description: "An error occurred while updating the reminder",
        variant: "destructive",
      });
    }
  };

  const getReminderTypeIcon = (type: string) => {
    switch (type) {
      case 'birthday':
        return <Calendar className="w-4 h-4 text-pink-500" />;
      case 'follow-up':
        return <Bell className="w-4 h-4 text-blue-500" />;
      case 'meeting':
        return <Clock className="w-4 h-4 text-purple-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };
  
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'completed':
        return 'success';
      case 'overdue':
        return 'destructive';
      default:
        return 'secondary';
    }
  };
  
  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-[70vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading reminders...</p>
      </div>
    );
  }
  
  return (
    <div className="container py-6 mx-auto">
      <div className="flex flex-col space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Reminders</h1>
            <p className="text-muted-foreground mt-1">Keep track of important dates and follow-ups</p>
          </div>
          
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1">
                  <Filter className="w-4 h-4" />
                  {activeFilter === 'all' ? 'All Types' : 
                    activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuLabel>Filter By Type</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setActiveFilter('all')}>
                  All Types
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveFilter('birthday')}>
                  Birthday
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveFilter('follow-up')}>
                  Follow-up
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveFilter('meeting')}>
                  Meeting
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveFilter('task')}>
                  Task
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button 
              onClick={() => setIsCreateModalOpen(true)}
              className="gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Reminder
            </Button>
          </div>
        </div>
        
        {/* Reminders List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Your Reminders</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredReminders.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  {activeFilter === 'all' 
                    ? "No Reminders Found" 
                    : `No ${activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)} Reminders`}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {activeFilter === 'all'
                    ? "You don't have any reminders set up yet."
                    : `You don't have any ${activeFilter} reminders set up yet.`}
                </p>
                <Button onClick={() => setIsCreateModalOpen(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Reminder
                </Button>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[100px]">Type</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Related To</TableHead>
                      <TableHead className="w-[150px]">Due Date</TableHead>
                      <TableHead className="w-[120px]">Status</TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReminders.map((reminder) => (
                      <TableRow key={reminder.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {getReminderTypeIcon(reminder.type)}
                            <span>{reminder.type.charAt(0).toUpperCase() + reminder.type.slice(1)}</span>
                          </div>
                        </TableCell>
                        <TableCell>{reminder.message}</TableCell>
                        <TableCell>
                          {reminder.customer && (
                            <span>{reminder.customer.firstName} {reminder.customer.lastName}</span>
                          )}
                          {reminder.deal && (
                            <span>{reminder.deal.name}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                            {format(new Date(reminder.dueDate), 'MMM dd, yyyy')}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(reminder.status)}>
                            {reminder.status.charAt(0).toUpperCase() + reminder.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {reminder.status !== 'completed' && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleMarkComplete(reminder.id)}
                              className="w-full"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Complete
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Create Reminder Modal */}
      <FormModal
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        title="Create New Reminder"
      >
        <CreateReminderForm 
          businessId={businessId} 
          onSuccess={() => {
            setIsCreateModalOpen(false);
            fetchReminders();
          }}
        />
      </FormModal>
    </div>
  );
} 